import React, { useState, useEffect, useCallback } from 'react';
import { food as foodApi, customFoods as customFoodsApi } from '../utils/api';
import { localToday, tzOffset } from '../utils/date';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';

const GOAL_DEFAULTS = {
  calories: 2000, protein_g: 150, carbs_g: 300,
  fat_g: 65, sodium_mg: 2300, sugar_g: 50, fiber_g: 38,
};

const GOAL_META = {
  calories:  { label: 'Calories', unit: '',   color: 'var(--accent-orange)'  },
  protein_g: { label: 'Protein',  unit: 'g',  color: 'var(--accent-cyan)'    },
  carbs_g:   { label: 'Carbs',    unit: 'g',  color: 'var(--accent-gold)'    },
  fat_g:     { label: 'Fat',      unit: 'g',  color: 'var(--accent-purple)'  },
  sodium_mg: { label: 'Sodium',   unit: 'mg', color: 'var(--accent-red)'     },
  sugar_g:   { label: 'Sugar',    unit: 'g',  color: 'var(--accent-magenta)' },
};

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'meal'];
const MEAL_ICONS = { breakfast: '🌅', lunch: '☀️', dinner: '🌙', snack: '🍎', meal: '🍽️' };
const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack', 'meal'];

function NutrientBar({ label, value, goal, color, unit }) {
  const pct  = goal > 0 ? Math.min(105, (value / goal) * 100) : 0;
  const over = value > goal && goal > 0;
  return (
    <div className="nutrient-bar">
      <div className="nutrient-bar-labels">
        <span style={{ color: over ? 'var(--accent-red)' : color }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem' }}>
          <span style={{ color: over ? 'var(--accent-red)' : 'var(--text-primary)' }}>{Math.round(value)}</span>
          <span style={{ color: 'var(--text-muted)' }}>/{goal}{unit}</span>
        </span>
      </div>
      <div className="nutrient-bar-track">
        <div className="nutrient-bar-fill" style={{
          width: `${pct}%`,
          background: over ? 'var(--accent-red)' : color,
          boxShadow: `0 0 6px ${over ? 'var(--accent-red)' : color}88`,
        }} />
      </div>
    </div>
  );
}

const emptyForm = {
  food_name: '', meal_type: 'meal', serving_size: 1, serving_unit: 'serving',
  calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sodium_mg: 0, sugar_g: 0, fiber_g: 0,
};

// Nutrient fields shared between log modal and manage modal
const NUTRIENT_FIELDS = [
  { key: 'calories',  label: 'Calories',   step: 1,   color: 'var(--accent-orange)'  },
  { key: 'protein_g', label: 'Protein (g)', step: 0.1, color: 'var(--accent-cyan)'    },
  { key: 'carbs_g',   label: 'Carbs (g)',   step: 0.1, color: 'var(--accent-gold)'    },
  { key: 'fat_g',     label: 'Fat (g)',     step: 0.1, color: 'var(--accent-purple)'  },
  { key: 'sodium_mg', label: 'Sodium (mg)', step: 1,   color: 'var(--accent-red)'     },
  { key: 'sugar_g',   label: 'Sugar (g)',   step: 0.1, color: 'var(--accent-magenta)' },
  { key: 'fiber_g',   label: 'Fiber (g)',   step: 0.1, color: 'var(--accent-green)'   },
];

export default function FoodLogPage({ activePlayerId, activePlayer }) {
  const toast = useToast();

  const [date,       setDate]       = useState(localToday);
  const [logs,       setLogs]       = useState([]);
  const [totals,     setTotals]     = useState(null);
  const [loading,    setLoading]    = useState(true);

  // Log modal state
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const [baseN,      setBaseN]      = useState(null);
  const [baseS,      setBaseS]      = useState(1);

  // Search state (inside log modal)
  const [searchTab,  setSearchTab]  = useState('database'); // 'database' | 'myfoods'
  const [searchQ,    setSearchQ]    = useState('');
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [myFoodsQ,   setMyFoodsQ]   = useState('');
  const [myFoods,    setMyFoods]    = useState([]);
  const [myFoodsLoading, setMyFoodsLoading] = useState(false);

  // Manage My Foods modal state
  const [showManage,    setShowManage]    = useState(false);
  const [manageSearch,  setManageSearch]  = useState('');
  const [manageList,    setManageList]    = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [editingFood,   setEditingFood]   = useState(null); // custom food being edited
  const [foodForm,      setFoodForm]      = useState(null); // form for editing a custom food

  const goals = {
    calories:  activePlayer?.goal_calories  || GOAL_DEFAULTS.calories,
    protein_g: activePlayer?.goal_protein_g || GOAL_DEFAULTS.protein_g,
    carbs_g:   activePlayer?.goal_carbs_g   || GOAL_DEFAULTS.carbs_g,
    fat_g:     activePlayer?.goal_fat_g     || GOAL_DEFAULTS.fat_g,
    sodium_mg: activePlayer?.goal_sodium_mg || GOAL_DEFAULTS.sodium_mg,
    sugar_g:   activePlayer?.goal_sugar_g   || GOAL_DEFAULTS.sugar_g,
  };

  // ── Load food log + totals ────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!activePlayerId) return;
    setLoading(true);
    try {
      const tz = tzOffset();
      const [l, t] = await Promise.all([
        foodApi.list(activePlayerId, date, tz),
        foodApi.totals(activePlayerId, date, tz),
      ]);
      setLogs(l); setTotals(t);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activePlayerId, date]);

  useEffect(() => { load(); }, [load]);

  // ── Load My Foods list (used in two places) ───────────────────────────────
  const loadMyFoods = useCallback(async (search = '') => {
    setMyFoodsLoading(true);
    try {
      const r = await customFoodsApi.list(search);
      setMyFoods(r);
    } catch (e) { console.error(e); }
    finally { setMyFoodsLoading(false); }
  }, []);

  // Load my foods when the tab is switched to it
  useEffect(() => {
    if (searchTab === 'myfoods' && showModal && !editing) {
      loadMyFoods(myFoodsQ);
    }
  }, [searchTab, showModal, editing, myFoodsQ, loadMyFoods]);

  // ── Load manage list ──────────────────────────────────────────────────────
  const loadManageList = useCallback(async (search = '') => {
    setManageLoading(true);
    try {
      setManageList(await customFoodsApi.list(search));
    } catch (e) { console.error(e); }
    finally { setManageLoading(false); }
  }, []);

  useEffect(() => {
    if (showManage) loadManageList(manageSearch);
  }, [showManage, manageSearch, loadManageList]);

  // ── USDA/FatSecret search ─────────────────────────────────────────────────
  const searchFood = async () => {
    if (!searchQ.trim()) return;
    setSearching(true); setResults([]);
    try {
      const r = await foodApi.search(searchQ);
      setResults(r);
      if (!r.length) toast('No results. Try different keywords.', 'info');
    } catch { toast('Food search failed. Try again.', 'error'); }
    finally { setSearching(false); }
  };

  // ── Select a food from any source (USDA/FatSecret/My Foods) ──────────────
  const selectFood = (item) => {
    // Normalise field names — custom foods use same names as USDA, but let's be safe
    const n = {
      calories:  Number(item.calories  || item.calories_per_serving  || 0),
      protein_g: Number(item.protein_g || 0),
      carbs_g:   Number(item.carbs_g   || 0),
      fat_g:     Number(item.fat_g     || 0),
      sodium_mg: Number(item.sodium_mg || 0),
      sugar_g:   Number(item.sugar_g   || 0),
      fiber_g:   Number(item.fiber_g   || 0),
    };
    setBaseN(n);
    setBaseS(Number(item.serving_size) || 1);
    setForm(f => ({
      ...f,
      food_name:    item.description || item.name || '',
      fdc_id:       item.fdc_id || '',
      serving_size: Number(item.serving_size) || 1,
      serving_unit: item.serving_unit || 'serving',
      ...n,
    }));
    setResults([]);
    setSearchQ('');
  };

  // ── Serving size scaling ──────────────────────────────────────────────────
  const handleServing = (v) => {
    const s = parseFloat(v) || 0;
    if (baseN && baseS > 0 && s > 0) {
      const r = s / baseS;
      setForm(f => ({
        ...f, serving_size: v,
        calories:  +(baseN.calories  * r).toFixed(1),
        protein_g: +(baseN.protein_g * r).toFixed(1),
        carbs_g:   +(baseN.carbs_g   * r).toFixed(1),
        fat_g:     +(baseN.fat_g     * r).toFixed(1),
        sodium_mg: +(baseN.sodium_mg * r).toFixed(1),
        sugar_g:   +(baseN.sugar_g   * r).toFixed(1),
        fiber_g:   +(baseN.fiber_g   * r).toFixed(1),
      }));
    } else { setForm(f => ({ ...f, serving_size: v })); }
  };

  // ── Open log modal ────────────────────────────────────────────────────────
  const openCreate = (defaultMeal) => {
    setEditing(null);
    setForm({ ...emptyForm, meal_type: defaultMeal || 'meal' });
    setSearchQ(''); setResults([]);
    setMyFoodsQ(''); setMyFoods([]);
    setSearchTab('database');
    setBaseN(null); setBaseS(1);
    setShowModal(true);
  };

  const openEdit = (log) => {
    setEditing(log); setBaseN(null); setBaseS(1);
    setForm({
      food_name: log.food_name, meal_type: log.meal_type || 'meal',
      serving_size: log.serving_size, serving_unit: log.serving_unit,
      calories: log.calories, protein_g: log.protein_g, carbs_g: log.carbs_g,
      fat_g: log.fat_g, sodium_mg: log.sodium_mg, sugar_g: log.sugar_g, fiber_g: log.fiber_g,
    });
    setShowModal(true);
  };

  // ── Save food log entry ───────────────────────────────────────────────────
  const save = async () => {
    if (!form.food_name.trim()) { toast('Food name is required', 'error'); return; }
    try {
      if (editing) {
        await foodApi.update(editing.id, { ...form, logged_at: editing.logged_at });
        toast('Food entry updated!', 'success');
      } else {
        await foodApi.create({
          ...form, player_id: activePlayerId,
          logged_at: new Date(date + 'T12:00:00').toISOString(),
        });
        toast(`Logged ${form.food_name}! (${Math.round(form.calories)} cal)`, 'success');
      }
      setShowModal(false); load();
    } catch (e) { toast(e.message, 'error'); }
  };

  // ── Save current form to My Foods ─────────────────────────────────────────
  const saveToMyFoods = async () => {
    if (!form.food_name.trim()) { toast('Enter a food name first', 'error'); return; }
    try {
      await customFoodsApi.create({
        name:         form.food_name.trim(),
        serving_size: form.serving_size,
        serving_unit: form.serving_unit,
        calories:     form.calories,
        protein_g:    form.protein_g,
        carbs_g:      form.carbs_g,
        fat_g:        form.fat_g,
        sodium_mg:    form.sodium_mg,
        sugar_g:      form.sugar_g,
        fiber_g:      form.fiber_g,
      });
      toast(`"${form.food_name}" saved to My Foods!`, 'success');
    } catch (e) {
      // 409 = duplicate — still a useful message
      toast(e.message, e.message.includes('already') ? 'info' : 'error');
    }
  };

  // ── Remove a food log entry ───────────────────────────────────────────────
  const remove = async (log) => {
    if (!window.confirm(`Remove "${log.food_name}"?`)) return;
    try { await foodApi.delete(log.id); toast('Removed.', 'info'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  // ── Manage My Foods — save edited custom food ─────────────────────────────
  const saveCustomFood = async () => {
    if (!foodForm?.name?.trim()) { toast('Food name is required', 'error'); return; }
    try {
      await customFoodsApi.update(editingFood.id, foodForm);
      toast('Custom food updated!', 'success');
      setEditingFood(null); setFoodForm(null);
      loadManageList(manageSearch);
    } catch (e) { toast(e.message, 'error'); }
  };

  const deleteCustomFood = async (food) => {
    if (!window.confirm(`Delete "${food.name}" from My Foods?`)) return;
    try {
      await customFoodsApi.delete(food.id);
      toast(`"${food.name}" deleted.`, 'info');
      loadManageList(manageSearch);
    } catch (e) { toast(e.message, 'error'); }
  };

  // ── Date navigation ───────────────────────────────────────────────────────
  const shiftDate = (days) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + days);
    setDate([
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
    ].join('-'));
  };

  const isToday = date === localToday();

  const grouped = MEAL_ORDER.reduce((acc, mt) => {
    const items = logs.filter(l => (l.meal_type || 'meal') === mt);
    if (items.length) acc[mt] = items;
    return acc;
  }, {});

  if (!activePlayerId) return <div className="empty-state"><p>Select a player</p></div>;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">🍖 Food Log</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setShowManage(true)}>📋 My Foods</button>
          <button className="btn btn-primary" onClick={() => openCreate()}>+ Log Food</button>
        </div>
      </div>

      {/* Date navigator */}
      <div className="date-nav">
        <button className="btn btn-sm" onClick={() => shiftDate(-1)}>◀</button>
        <div className="date-nav-center">
          <input type="date" className="form-input date-nav-input"
            value={date} onChange={e => setDate(e.target.value)} />
          {!isToday && (
            <button className="btn btn-sm" onClick={() => setDate(localToday())}>Today</button>
          )}
        </div>
        <button className="btn btn-sm" onClick={() => shiftDate(1)} disabled={isToday}>▶</button>
      </div>

      {/* Daily totals + progress bars */}
      {totals && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
            {[
              { k: 'total_calories', l: 'Calories', cls: 'kpi-orange',  s: ''   },
              { k: 'total_protein',  l: 'Protein',  cls: 'kpi-cyan',    s: 'g'  },
              { k: 'total_carbs',    l: 'Carbs',    cls: 'kpi-gold',    s: 'g'  },
              { k: 'total_fat',      l: 'Fat',      cls: 'kpi-purple',  s: 'g'  },
              { k: 'total_sodium',   l: 'Sodium',   cls: 'kpi-red',     s: 'mg' },
              { k: 'total_sugar',    l: 'Sugar',    cls: 'kpi-magenta', s: 'g'  },
            ].map(({ k, l, cls, s }) => (
              <div key={k} className={`kpi-card ${cls}`}>
                <div className="kpi-value">{Math.round(Number(totals[k]))}{s}</div>
                <div className="kpi-label">{l}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { key: 'calories',  totalsKey: 'total_calories' },
              { key: 'protein_g', totalsKey: 'total_protein'  },
              { key: 'carbs_g',   totalsKey: 'total_carbs'    },
              { key: 'fat_g',     totalsKey: 'total_fat'      },
              { key: 'sodium_mg', totalsKey: 'total_sodium'   },
              { key: 'sugar_g',   totalsKey: 'total_sugar'    },
            ].map(({ key, totalsKey }) => (
              <NutrientBar
                key={key}
                label={GOAL_META[key].label}
                value={Number(totals[totalsKey]) || 0}
                goal={goals[key]}
                color={GOAL_META[key].color}
                unit={GOAL_META[key].unit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Food log grouped by meal */}
      {loading ? (
        <div className="empty-state"><p>Loading logs...</p></div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <div className="icon">🍽️</div>
          <p>{isToday ? 'Nothing logged today. Time to eat!' : 'No food logged for this date.'}</p>
          <br /><button className="btn btn-primary" onClick={() => openCreate()}>+ Log Food</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(grouped).map(([mt, items]) => {
            const mtCal = items.reduce((s, i) => s + Number(i.calories), 0);
            return (
              <div className="card" key={mt}>
                <div className="meal-group-header">
                  <span className="meal-group-icon">{MEAL_ICONS[mt]}</span>
                  <span className="meal-group-name">{mt.charAt(0).toUpperCase() + mt.slice(1)}</span>
                  <span className="meal-group-cal">{Math.round(mtCal)} cal</span>
                  <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => openCreate(mt)}>+ Add</button>
                </div>
                <table className="data-table">
                  <thead>
                    <tr><th>Food</th><th>Cal</th><th>Prot</th><th>Carbs</th><th>Fat</th><th>Na</th><th></th></tr>
                  </thead>
                  <tbody>
                    {items.map(l => (
                      <tr key={l.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{l.food_name}</div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {l.serving_size} {l.serving_unit}
                          </div>
                        </td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{Math.round(Number(l.calories))}</td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{Math.round(Number(l.protein_g))}g</td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{Math.round(Number(l.carbs_g))}g</td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{Math.round(Number(l.fat_g))}g</td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{Math.round(Number(l.sodium_mg))}mg</td>
                        <td>
                          <div className="btn-group">
                            <button className="btn btn-sm btn-icon" onClick={() => openEdit(l)}>✎</button>
                            <button className="btn btn-sm btn-icon btn-danger" onClick={() => remove(l)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Log / Edit Modal ──────────────────────────────────────────────── */}
      {showModal && (
        <Modal
          title={editing ? '✎ Edit Food Entry' : '+ Log Food'}
          onClose={() => setShowModal(false)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 8 }}>
              {/* Save to My Foods button — shown when form has a name and we're not editing */}
              {!editing && form.food_name.trim() && (
                <button className="btn btn-sm" style={{ color: 'var(--accent-cyan)', borderColor: 'var(--accent-cyan)' }}
                  onClick={saveToMyFoods} title="Save this food's macros to My Foods for quick reuse">
                  💾 Save to My Foods
                </button>
              )}
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save}>{editing ? 'Save' : 'Log Food'}</button>
              </div>
            </div>
          }
        >
          {/* Search section — only shown when creating, not editing */}
          {!editing && (
            <div className="form-group">
              {/* Tab switcher: Database vs My Foods */}
              <div className="search-tabs">
                <button
                  className={`search-tab${searchTab === 'database' ? ' active' : ''}`}
                  onClick={() => setSearchTab('database')}
                >
                  🔍 Database
                </button>
                <button
                  className={`search-tab${searchTab === 'myfoods' ? ' active' : ''}`}
                  onClick={() => setSearchTab('myfoods')}
                >
                  ⭐ My Foods
                </button>
              </div>

              {/* Database search */}
              {searchTab === 'database' && (
                <>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input className="form-input" value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      placeholder="e.g. chicken breast, banana..."
                      onKeyDown={e => e.key === 'Enter' && searchFood()} />
                    <button className="btn btn-primary" onClick={searchFood}
                      disabled={searching} style={{ whiteSpace: 'nowrap' }}>
                      {searching ? '...' : 'Search'}
                    </button>
                  </div>
                  {results.length > 0 && (
                    <div className="search-results">
                      {results.map((item, i) => (
                        <div key={i} className="search-result-item" onClick={() => selectFood(item)}>
                          <div className="search-result-top">
                            <div className="name">{item.description}</div>
                            {item.source && (
                              <span className={`search-source-badge search-source-${item.source === 'FatSecret' ? 'fs' : 'usda'}`}>
                                {item.source === 'FatSecret' ? 'FS' : 'USDA'}
                              </span>
                            )}
                          </div>
                          <div className="meta">
                            {item.brand && `${item.brand} · `}
                            {Math.round(item.calories || 0)} cal · {Math.round(item.protein_g || 0)}g protein
                            {item.serving_size ? ` · per ${item.serving_size}${item.serving_unit}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* My Foods search */}
              {searchTab === 'myfoods' && (
                <>
                  <input className="form-input" style={{ marginTop: 8 }}
                    value={myFoodsQ}
                    onChange={e => setMyFoodsQ(e.target.value)}
                    placeholder="Filter saved foods..." />
                  <div className="search-results" style={{ marginTop: 6 }}>
                    {myFoodsLoading ? (
                      <div style={{ padding: '12px', fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                        Loading...
                      </div>
                    ) : myFoods.length === 0 ? (
                      <div style={{ padding: '12px', fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                        {myFoodsQ ? 'No matches.' : 'No saved foods yet. Fill in macros below and click 💾 Save to My Foods.'}
                      </div>
                    ) : (
                      myFoods.map(food => (
                        <div key={food.id} className="search-result-item" onClick={() => selectFood({ ...food, description: food.name })}>
                          <div className="search-result-top">
                            <div className="name">{food.name}</div>
                            <span className="search-source-badge" style={{ background: 'rgba(0,255,136,0.1)', color: 'var(--accent-green)', border: '1px solid rgba(0,255,136,0.3)' }}>
                              MY
                            </span>
                          </div>
                          <div className="meta">
                            {Math.round(food.calories)} cal · {Math.round(food.protein_g)}g protein
                            {` · per ${food.serving_size}${food.serving_unit}`}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Food name + meal type */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Food Name *</label>
              <input className="form-input" value={form.food_name}
                onChange={e => setForm({ ...form, food_name: e.target.value })}
                placeholder="e.g. Smash Burger" />
            </div>
            <div className="form-group">
              <label className="form-label">Meal</label>
              <select className="form-select" value={form.meal_type}
                onChange={e => setForm({ ...form, meal_type: e.target.value })}>
                {MEAL_TYPES.map(m => (
                  <option key={m} value={m}>
                    {MEAL_ICONS[m]} {m.charAt(0).toUpperCase() + m.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Serving size + unit */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Serving Size</label>
              <input className="form-input" type="number" step="0.1" min="0"
                value={form.serving_size} onChange={e => handleServing(e.target.value)} />
              {baseN && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--accent-cyan)', marginTop: 3 }}>
                  ↕ Nutrients scale automatically
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Unit</label>
              <input className="form-input" value={form.serving_unit}
                onChange={e => setForm({ ...form, serving_unit: e.target.value })}
                placeholder="g, oz, cup..." />
            </div>
          </div>

          {/* Nutrient inputs */}
          <div className="nutrient-grid">
            {NUTRIENT_FIELDS.map(({ key, label, step, color }) => (
              <div className="form-group" key={key}>
                <label className="form-label" style={{ color }}>{label}</label>
                <input className="form-input" type="number" step={step} min="0"
                  value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* ── Manage My Foods Modal ─────────────────────────────────────────── */}
      {showManage && (
        <Modal
          title="⭐ My Foods"
          onClose={() => { setShowManage(false); setEditingFood(null); setFoodForm(null); }}
          footer={
            editingFood ? (
              <>
                <button className="btn" onClick={() => { setEditingFood(null); setFoodForm(null); }}>← Back</button>
                <button className="btn btn-primary" onClick={saveCustomFood}>Save Changes</button>
              </>
            ) : (
              <button className="btn" onClick={() => setShowManage(false)}>Close</button>
            )
          }
        >
          {/* ── Editing a specific custom food ── */}
          {editingFood && foodForm ? (
            <>
              <div className="form-group">
                <label className="form-label">Food Name *</label>
                <input className="form-input" value={foodForm.name} autoFocus
                  onChange={e => setFoodForm({ ...foodForm, name: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Serving Size</label>
                  <input className="form-input" type="number" step="0.1" min="0"
                    value={foodForm.serving_size}
                    onChange={e => setFoodForm({ ...foodForm, serving_size: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <input className="form-input" value={foodForm.serving_unit}
                    onChange={e => setFoodForm({ ...foodForm, serving_unit: e.target.value })} />
                </div>
              </div>
              <div className="nutrient-grid">
                {NUTRIENT_FIELDS.map(({ key, label, step, color }) => (
                  <div className="form-group" key={key}>
                    <label className="form-label" style={{ color }}>{label}</label>
                    <input className="form-input" type="number" step={step} min="0"
                      value={foodForm[key]}
                      onChange={e => setFoodForm({ ...foodForm, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* ── List of saved foods ── */
            <>
              <input className="form-input" value={manageSearch}
                onChange={e => setManageSearch(e.target.value)}
                placeholder="Search saved foods..."
                style={{ marginBottom: 12 }} />

              {manageLoading ? (
                <div className="empty-state"><p>Loading...</p></div>
              ) : manageList.length === 0 ? (
                <div className="empty-state">
                  <div className="icon">⭐</div>
                  <p>{manageSearch ? 'No matches.' : 'No saved foods yet.'}</p>
                  <p style={{ marginTop: 8, fontSize: '0.7rem' }}>
                    Log a food, fill in its macros, then click 💾 Save to My Foods.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
                  {manageList.map(food => (
                    <div key={food.id} className="my-food-row">
                      <div className="my-food-info">
                        <div className="my-food-name">{food.name}</div>
                        <div className="my-food-meta">
                          {Math.round(food.calories)} cal · {Math.round(food.protein_g)}g P · {Math.round(food.carbs_g)}g C · {Math.round(food.fat_g)}g F
                          <span style={{ marginLeft: 6, color: 'var(--text-muted)' }}>per {food.serving_size}{food.serving_unit}</span>
                        </div>
                      </div>
                      <div className="btn-group">
                        <button className="btn btn-sm btn-icon"
                          onClick={() => {
                            setEditingFood(food);
                            setFoodForm({
                              name: food.name,
                              serving_size: food.serving_size, serving_unit: food.serving_unit,
                              calories: food.calories, protein_g: food.protein_g,
                              carbs_g: food.carbs_g, fat_g: food.fat_g,
                              sodium_mg: food.sodium_mg, sugar_g: food.sugar_g, fiber_g: food.fiber_g,
                            });
                          }}>✎</button>
                        <button className="btn btn-sm btn-icon btn-danger"
                          onClick={() => deleteCustomFood(food)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal>
      )}
    </div>
  );
}